// ============================================================
// prediction.ts — Client-side prediction and server reconciliation
// with smooth correction blending (exponential decay over 100ms)
// ============================================================

import { InputMessage, PlayerSnapshot } from './messages';

/** Minimal player state needed for prediction. */
export interface PlayerState {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  isCrouching: boolean;
  isGrounded: boolean;
  isSliding: boolean;
}

/** Function signature for applying a single input tick to a player state. */
export type ApplyInputFn = (state: PlayerState, input: InputMessage) => PlayerState;

// --- Circular input buffer ---

interface BufferEntry {
  sequence: number;
  input: InputMessage;
  predictedState: PlayerState;
}

const BUFFER_SIZE = 256;

export class InputBuffer {
  private entries: (BufferEntry | null)[] = new Array(BUFFER_SIZE).fill(null);
  private head = 0;
  private count = 0;

  store(seq: number, input: InputMessage, predictedState: PlayerState): void {
    const idx = this.head;
    this.entries[idx] = { sequence: seq, input, predictedState };
    this.head = (this.head + 1) % BUFFER_SIZE;
    if (this.count < BUFFER_SIZE) this.count++;
  }

  /**
   * Return all stored inputs with sequence > lastAckSeq,
   * ordered by sequence ascending.
   */
  getUnacknowledged(lastAckSeq: number): BufferEntry[] {
    const result: BufferEntry[] = [];

    // Walk backwards from head to collect up to `count` entries
    for (let i = 0; i < this.count; i++) {
      const idx = (this.head - 1 - i + BUFFER_SIZE) % BUFFER_SIZE;
      const entry = this.entries[idx];
      if (entry !== null && entry.sequence > lastAckSeq) {
        result.push(entry);
      }
    }

    // Sort ascending by sequence
    result.sort((a, b) => a.sequence - b.sequence);
    return result;
  }

  clear(): void {
    this.entries.fill(null);
    this.head = 0;
    this.count = 0;
  }
}

// --- Prediction system ---

/** Threshold (squared) below which we keep the prediction rather than correcting. */
const SNAP_THRESHOLD = 0.1;
const SNAP_THRESHOLD_SQ = SNAP_THRESHOLD * SNAP_THRESHOLD;

/** Duration to blend correction over (ms). */
const CORRECTION_BLEND_MS = 100;

export class PredictionSystem {
  private applyInput: ApplyInputFn;
  private buffer = new InputBuffer();
  private sequence = 0;

  /** Smoothing offset that gets blended out over time via exponential decay. */
  private correctionOffset = { x: 0, y: 0, z: 0 };

  /** Timestamp when the last correction was applied. */
  private correctionStartTime = 0;

  /** Last prediction error magnitude for debug. */
  private _lastPredictionError = 0;

  /** Callback to record prediction error for debug visualization. */
  public onPredictionError: ((error: number) => void) | null = null;

  constructor(applyInput: ApplyInputFn) {
    this.applyInput = applyInput;
  }

  /** Get the next sequence number and increment the counter. */
  getSequence(): number {
    return this.sequence++;
  }

  /**
   * Apply input locally for immediate feedback and store in the buffer.
   * Returns the predicted state after applying the input.
   */
  processInput(localPlayer: PlayerState, input: InputMessage, seq: number): PlayerState {
    const predicted = this.applyInput({ ...localPlayer }, input);
    this.buffer.store(seq, input, predicted);
    return predicted;
  }

  /**
   * Reconcile the local player with the authoritative server snapshot.
   * Returns the corrected player state.
   * Uses smooth correction: instead of snapping when error > threshold,
   * stores offset and blends it out over 100ms with exponential decay.
   */
  reconcile(localPlayer: PlayerState, serverSnapshot: PlayerSnapshot, ackSeq: number): PlayerState {
    // 1. Authoritative state from server
    const authState: PlayerState = {
      x: serverSnapshot.position.x,
      y: serverSnapshot.position.y,
      z: serverSnapshot.position.z,
      yaw: serverSnapshot.yaw,
      pitch: serverSnapshot.pitch,
      isCrouching: serverSnapshot.isCrouching,
      isGrounded: serverSnapshot.isGrounded ?? serverSnapshot.position.y <= 0.01,
      isSliding: serverSnapshot.isSliding ?? false,
    };

    // 2. Get all unacknowledged inputs (after the server's last processed sequence)
    const unacked = this.buffer.getUnacknowledged(ackSeq);

    // 3. Re-simulate from authoritative state
    let reconciled = { ...authState };
    for (const entry of unacked) {
      reconciled = this.applyInput({ ...reconciled }, entry.input);
    }

    // 4. Compare reconciled position with current predicted position
    const dx = reconciled.x - localPlayer.x;
    const dy = reconciled.y - localPlayer.y;
    const dz = reconciled.z - localPlayer.z;
    const distSq = dx * dx + dy * dy + dz * dz;
    const error = Math.sqrt(distSq);

    // Record prediction error for debug
    this._lastPredictionError = error;
    this.onPredictionError?.(error);

    if (distSq < SNAP_THRESHOLD_SQ) {
      // Close enough — keep the current predicted position for smooth visuals
      return localPlayer;
    }

    // 5. Smooth correction: accumulate the error as an offset and blend it out
    // over CORRECTION_BLEND_MS using exponential decay.
    // Use separate vertical threshold: snap Y if error > 0.5 (e.g. teleport/respawn)
    if (Math.abs(dy) > 0.5) {
      // Large vertical discrepancy — snap Y directly, don't accumulate offset
      this.correctionOffset.y = 0;
    } else {
      this.correctionOffset.y += localPlayer.y - reconciled.y;
    }
    this.correctionOffset.x += localPlayer.x - reconciled.x;
    this.correctionOffset.z += localPlayer.z - reconciled.z;
    this.correctionStartTime = performance.now();

    return reconciled;
  }

  /**
   * Call each frame to blend out the correction offset smoothly.
   * Uses exponential decay so ~95% is blended within CORRECTION_BLEND_MS.
   * @param dtMs - delta time in milliseconds since last frame
   * Returns the visual offset to add to the player's rendered position.
   */
  getCorrectionOffset(dtMs?: number): { x: number; y: number; z: number } {
    // Use exponential decay
    // halflife ~ CORRECTION_BLEND_MS / 3 so that ~95% decays in CORRECTION_BLEND_MS
    const halflife = CORRECTION_BLEND_MS / 3;
    const dt = dtMs ?? 16.67; // default ~60fps
    const factor = Math.exp(-dt / halflife);

    this.correctionOffset.x *= factor;
    this.correctionOffset.y *= factor;
    this.correctionOffset.z *= factor;

    // Zero out if negligible
    const magSq =
      this.correctionOffset.x * this.correctionOffset.x +
      this.correctionOffset.y * this.correctionOffset.y +
      this.correctionOffset.z * this.correctionOffset.z;

    if (magSq < 0.0001) {
      this.correctionOffset.x = 0;
      this.correctionOffset.y = 0;
      this.correctionOffset.z = 0;
    }

    return { ...this.correctionOffset };
  }

  /** Get the last prediction error magnitude. */
  getLastPredictionError(): number {
    return this._lastPredictionError;
  }

  /** Reset state (e.g. on respawn or reconnect). */
  reset(): void {
    this.buffer.clear();
    this.sequence = 0;
    this.correctionOffset = { x: 0, y: 0, z: 0 };
    this._lastPredictionError = 0;
  }
}
